// Renders a controls row (prev/next arrows + a slide indicator) below every
// .pe-carousel, keeps the active indicator in sync with whichever slide is
// currently in view, and auto-advances one slide every AUTOPLAY_MS (wrapping
// back to the first slide at the end). The controls sit under the slides on
// purpose so they never cover or shrink the images. The indicator comes in
// three modes resolved from classes on .pe-carousel:
//   (default)             — image thumbnail strip, drag-to-scroll with mouse
//   pe-carousel-nav-dots  — classic dots
//   pe-carousel-nav-none  — no indicator (legacy pe-carousel-dots-off too)
// The DB stores only the bare <div class="pe-carousel">/<div
// class="pe-carousel-slide"> markup; controls and any autoplay state are
// added at render time and never persisted (other opt-outs:
// pe-carousel-arrows-off, pe-carousel-autoplay-off). New carousels are picked
// up two ways: in-page Livewire morphs via a MutationObserver, and full-page
// wire:navigate swaps via the livewire:navigated event (the observer can't see
// those — navigate replaces <body>, orphaning it).

const INIT_MARK = '__peCarouselDotsInit'
const DOTS_MARK = '__peCarouselDots'
const DOT_PIN_MARK = '__peCarouselDotPinned'
const AUTOPLAY_MS = 2000
const USER_PAUSE_MS = 5000

function initCarousel(carousel) {
    if (carousel[INIT_MARK]) {
        return
    }
    const slides = Array.from(carousel.querySelectorAll('.pe-carousel-slide'))
    if (slides.length < 2) {
        return
    }
    carousel[INIT_MARK] = true

    const navMode = resolveNavMode(carousel)
    const arrowsOff = carousel.classList.contains('pe-carousel-arrows-off')

    let indicator = null
    if (navMode === 'dots') {
        indicator = buildDots(carousel, slides)
    } else if (navMode === 'thumbs') {
        indicator = buildThumbs(carousel, slides)
    }

    // Arrows overlay the left/right edges of the slides, vertically centered
    // and revealed on hover only: the carousel is wrapped in a positioning
    // shell the arrows anchor to, so the slides keep 100% width. A Livewire
    // morph that re-renders the content throws the shell away together with
    // the carousel; the MutationObserver re-initializes the fresh copy.
    let shell = null
    if (!arrowsOff) {
        shell = document.createElement('div')
        shell.className = 'pe-carousel-shell'
        carousel.insertAdjacentElement('beforebegin', shell)
        shell.appendChild(createArrow(carousel, -1))
        shell.appendChild(carousel)
        shell.appendChild(createArrow(carousel, 1))
    }

    let controls = null
    if (indicator) {
        controls = document.createElement('div')
        controls.className = 'pe-carousel-controls' + (navMode === 'thumbs' ? ' pe-carousel-controls-thumbs' : '')
        controls.appendChild(indicator)
        ;(shell || carousel).insertAdjacentElement('afterend', controls)
    }

    if (indicator) {
        carousel[DOTS_MARK] = indicator

        let timer = null
        carousel.addEventListener('scroll', () => {
            // While a programmatic scroll is in flight the target indicator
            // was already activated in scrollCarouselToSlide(); nearest-slide
            // updates would flick it back to the departing slide mid-glide.
            if (!carousel[DOT_PIN_MARK]) {
                updateActiveDot(carousel, indicator)
            }
            clearTimeout(timer)
            timer = setTimeout(() => {
                carousel[DOT_PIN_MARK] = false
                updateActiveDot(carousel, indicator)
            }, 80)
        }, { passive: true })

        // Manual input takes indicator control back from a pinned
        // programmatic scroll right away.
        ;['wheel', 'touchstart', 'pointerdown'].forEach((evt) => {
            carousel.addEventListener(evt, () => {
                carousel[DOT_PIN_MARK] = false
            }, { passive: true })
        })

        updateActiveDot(carousel, indicator)
    }

    setupAutoplay(carousel, controls)
}

function resolveNavMode(carousel) {
    const cls = carousel.classList
    // pe-carousel-dots-off predates the nav-* modes; keep honoring it as
    // "no indicator" so older saved content doesn't suddenly grow thumbnails.
    if (cls.contains('pe-carousel-nav-none') || cls.contains('pe-carousel-dots-off')) {
        return 'none'
    }
    if (cls.contains('pe-carousel-nav-dots')) {
        return 'dots'
    }
    return 'thumbs'
}

function buildDots(carousel, slides) {
    const dots = document.createElement('div')
    dots.className = 'pe-carousel-dots'
    dots.setAttribute('role', 'tablist')
    dots.setAttribute('aria-label', 'Carousel pagination')

    slides.forEach((slide, idx) => {
        const dot = document.createElement('button')
        dot.type = 'button'
        dot.className = 'pe-carousel-dot' + (idx === 0 ? ' pe-carousel-dot-active' : '')
        dot.setAttribute('role', 'tab')
        dot.setAttribute('aria-label', `Go to slide ${idx + 1}`)
        dot.addEventListener('click', (event) => {
            event.preventDefault()
            scrollCarouselToSlide(carousel, slide, true)
        })
        dots.appendChild(dot)
    })

    return dots
}

function buildThumbs(carousel, slides) {
    const strip = document.createElement('div')
    strip.className = 'pe-carousel-thumbs'
    strip.setAttribute('role', 'tablist')
    strip.setAttribute('aria-label', 'Carousel thumbnails')

    slides.forEach((slide, idx) => {
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.className = 'pe-carousel-thumb' + (idx === 0 ? ' pe-carousel-thumb-active' : '')
        btn.setAttribute('role', 'tab')
        btn.setAttribute('aria-label', `Go to slide ${idx + 1}`)

        const slideImg = slide.querySelector('img')
        if (slideImg) {
            const thumbImg = document.createElement('img')
            thumbImg.src = slideImg.currentSrc || slideImg.getAttribute('src') || ''
            thumbImg.alt = ''
            thumbImg.loading = 'lazy'
            thumbImg.draggable = false
            btn.appendChild(thumbImg)
        } else {
            btn.textContent = String(idx + 1)
        }

        btn.addEventListener('click', (event) => {
            event.preventDefault()
            // A drag that happens to end on a thumb is not a click.
            if (strip.__peDragged) {
                return
            }
            scrollCarouselToSlide(carousel, slide, true)
        })
        strip.appendChild(btn)
    })

    enableDragScroll(strip)
    return strip
}

// Hold-and-drag horizontal scrolling for the thumbnail strip (mouse only,
// touch devices pan natively via overflow scrolling).
function enableDragScroll(strip) {
    let dragging = false
    let startX = 0
    let startScrollLeft = 0

    strip.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) {
            return
        }
        dragging = true
        startX = event.clientX
        startScrollLeft = strip.scrollLeft
        strip.__peDragged = false
        strip.classList.add('pe-carousel-thumbs-dragging')
    })

    window.addEventListener('pointermove', (event) => {
        if (!dragging) {
            return
        }
        const dx = event.clientX - startX
        if (Math.abs(dx) > 5) {
            strip.__peDragged = true
        }
        strip.scrollLeft = startScrollLeft - dx
    }, { passive: true })

    const release = () => {
        if (!dragging) {
            return
        }
        dragging = false
        strip.classList.remove('pe-carousel-thumbs-dragging')
        // The suppressed click on a thumb fires right after pointerup;
        // clear the flag once that's done.
        setTimeout(() => { strip.__peDragged = false }, 0)
    }
    window.addEventListener('pointerup', release)
    window.addEventListener('pointercancel', release)
}

// Keep the active thumbnail visible (centered) in the strip without ever
// scrolling the page — same getBoundingClientRect approach as the slides.
function revealThumb(strip, thumb) {
    if (strip.classList.contains('pe-carousel-thumbs-dragging')) {
        return
    }
    const stripRect = strip.getBoundingClientRect()
    const thumbRect = thumb.getBoundingClientRect()
    const delta = (thumbRect.left + thumbRect.width / 2) - (stripRect.left + stripRect.width / 2)
    const left = Math.max(0, Math.min(strip.scrollWidth - strip.clientWidth, strip.scrollLeft + delta))
    if (Math.abs(left - strip.scrollLeft) < 1) {
        return
    }
    strip.scrollTo({ left, behavior: 'smooth' })
}

function createArrow(carousel, direction) {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'pe-carousel-arrow ' + (direction < 0 ? 'pe-carousel-arrow-prev' : 'pe-carousel-arrow-next')
    btn.setAttribute('aria-label', direction < 0 ? 'Previous slide' : 'Next slide')
    btn.innerHTML = direction < 0
        ? '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>'
        : '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>'
    btn.addEventListener('click', (event) => {
        event.preventDefault()
        const slides = carousel.querySelectorAll('.pe-carousel-slide')
        if (slides.length < 2) {
            return
        }
        const currentIdx = activeSlideIndex(carousel)
        const lastIdx = slides.length - 1
        const isWrap = direction > 0 ? currentIdx >= lastIdx : currentIdx <= 0
        const nextIdx = isWrap ? (direction > 0 ? 0 : lastIdx) : currentIdx + direction
        // Mirror autoplay: glide to the adjacent slide, jump on wrap-around
        // so the scroll doesn't sweep across the whole strip.
        scrollCarouselToSlide(carousel, slides[nextIdx], !isWrap)
    })
    return btn
}

function scrollCarouselToSlide(carousel, slide, smooth) {
    // Activate the target dot the moment the transition starts instead of
    // waiting for the scroll to settle on the new slide.
    const dots = carousel[DOTS_MARK]
    if (dots) {
        const idx = Array.from(carousel.querySelectorAll('.pe-carousel-slide')).indexOf(slide)
        if (idx >= 0) {
            carousel[DOT_PIN_MARK] = true
            setActiveDot(dots, idx)
        }
    }

    // Avoid slide.scrollIntoView(): even with block:'nearest' it scrolls
    // ancestors (the page) vertically when the carousel is out of view.
    // Use getBoundingClientRect so the math is independent of offsetParent
    // (the carousel has no `position` set, so offsetLeft would be wrong).
    const carouselRect = carousel.getBoundingClientRect()
    const slideRect = slide.getBoundingClientRect()
    const delta = (slideRect.left + slideRect.width / 2) - (carouselRect.left + carouselRect.width / 2)
    const left = Math.max(0, Math.min(carousel.scrollWidth - carousel.clientWidth, carousel.scrollLeft + delta))
    carousel.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' })
}

function activeSlideIndex(carousel) {
    const slides = carousel.querySelectorAll('.pe-carousel-slide')
    if (slides.length === 0) {
        return -1
    }
    // getBoundingClientRect, NOT offsetLeft: the slides' offsetParent is some
    // positioned ancestor up the page, which biased every slide center by a
    // constant. Past half a slide width of bias the last slide was detected
    // as last-but-one, so the next arrow (and autoplay) targeted the slide it
    // was already on — no movement, no wrap, "dead" arrow. Rects keep this in
    // the same coordinate space scrollCarouselToSlide() uses.
    const carouselRect = carousel.getBoundingClientRect()
    const center = carouselRect.left + carouselRect.width / 2
    let bestIdx = 0
    let bestDist = Infinity
    slides.forEach((slide, idx) => {
        const slideRect = slide.getBoundingClientRect()
        const dist = Math.abs((slideRect.left + slideRect.width / 2) - center)
        if (dist < bestDist) {
            bestDist = dist
            bestIdx = idx
        }
    })
    return bestIdx
}

function setActiveDot(indicator, idx) {
    const isThumbs = indicator.classList.contains('pe-carousel-thumbs')
    const itemSelector = isThumbs ? '.pe-carousel-thumb' : '.pe-carousel-dot'
    const activeClass = isThumbs ? 'pe-carousel-thumb-active' : 'pe-carousel-dot-active'
    const previous = indicator.querySelector('.' + activeClass)
    let active = null
    indicator.querySelectorAll(itemSelector).forEach((el, i) => {
        el.classList.toggle(activeClass, i === idx)
        if (i === idx) {
            active = el
        }
    })
    if (isThumbs && active && active !== previous) {
        revealThumb(indicator, active)
    }
}

function updateActiveDot(carousel, indicator) {
    const idx = activeSlideIndex(carousel)
    if (idx < 0) {
        return
    }
    setActiveDot(indicator, idx)
}

function resolveAutoplayMs(carousel) {
    const cls = carousel.className || ''
    if (/\bpe-carousel-autoplay-off\b/.test(cls)) {
        return 0
    }
    const match = cls.match(/\bpe-carousel-autoplay-(\d+)\b/)
    if (match) {
        const seconds = parseInt(match[1], 10)
        return seconds > 0 ? seconds * 1000 : 0
    }
    return AUTOPLAY_MS
}

function setupAutoplay(carousel, controls) {
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduceMotion) {
        return
    }

    const intervalMs = resolveAutoplayMs(carousel)
    if (intervalMs <= 0) {
        return
    }

    let hovering = false
    let userPausedUntil = 0

    const isPaused = () => hovering || Date.now() < userPausedUntil
    const pauseForUser = () => { userPausedUntil = Date.now() + USER_PAUSE_MS }

    setInterval(() => {
        if (isPaused()) {
            return
        }
        if (document.hidden) {
            return
        }
        const slides = carousel.querySelectorAll('.pe-carousel-slide')
        if (slides.length < 2) {
            return
        }
        const currentIdx = activeSlideIndex(carousel)
        const isWrap = currentIdx >= slides.length - 1
        const nextIdx = isWrap ? 0 : currentIdx + 1
        scrollCarouselToSlide(carousel, slides[nextIdx], !isWrap)
    }, intervalMs)

    // Hover anywhere on the shell (arrows included) pauses autoplay; without
    // a shell (arrows off) the carousel itself is the hover root.
    const shell = carousel.parentElement && carousel.parentElement.classList.contains('pe-carousel-shell')
        ? carousel.parentElement
        : carousel
    shell.addEventListener('mouseenter', () => { hovering = true })
    shell.addEventListener('mouseleave', () => { hovering = false })
    if (shell !== carousel) {
        shell.addEventListener('click', pauseForUser)
    }
    if (controls) {
        controls.addEventListener('mouseenter', () => { hovering = true })
        controls.addEventListener('mouseleave', () => { hovering = false })
        controls.addEventListener('click', pauseForUser)
    }
    ;['wheel', 'touchstart', 'pointerdown'].forEach((evt) => {
        carousel.addEventListener(evt, pauseForUser, { passive: true })
    })
}

function initAll(root) {
    const scope = root || document
    if (scope.querySelectorAll) {
        scope.querySelectorAll('.pe-carousel').forEach(initCarousel)
    }
}

let observer = null

// (Re)bind the MutationObserver to the live document.body. A bound observer
// only sees mutations inside the node it was attached to, and wire:navigate
// swaps the whole <body> out — orphaning an observer bound to the previous
// body. So we disconnect and re-observe the fresh body on every (re)start
// rather than observing a single, possibly-stale body once.
function observeBody() {
    if (observer) {
        observer.disconnect()
    }
    observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType !== 1) {
                    continue
                }
                if (node.classList && node.classList.contains('pe-carousel')) {
                    initCarousel(node)
                }
                if (node.querySelectorAll) {
                    node.querySelectorAll('.pe-carousel').forEach(initCarousel)
                }
            }
        }
    })
    observer.observe(document.body, { childList: true, subtree: true })
}

function start() {
    initAll(document)
    observeBody()
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start)
} else {
    start()
}

// wire:navigate does SPA navigation: it replaces <body> without firing
// DOMContentLoaded, so a carousel on the freshly navigated-to page would never
// be enhanced — you'd see only the first bare slide, no thumbs/arrows. Re-run
// init and re-bind the observer after each navigation. initCarousel's INIT_MARK
// guard keeps already-initialized carousels from being processed twice, and
// Livewire firing this once on the initial load is harmless (idempotent).
document.addEventListener('livewire:navigated', start)
